//frontend/src/components/Chat/hooks/useMatrixVoiceRecorder.js микрофон + запись + отправка voice


import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

function getSupportedAudioMimeType() {
  if (typeof MediaRecorder === "undefined") {
    return "";
  }

  const types = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ];

  return (
    types.find((type) =>
      MediaRecorder.isTypeSupported(type)
    ) || ""
  );
}

function getAudioExtension(mimeType) {
  if (mimeType.includes("ogg")) {
    return "ogg";
  }

  return "webm";
}

export default function useMatrixVoiceRecorder(client, roomId) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(null);

  const clearError = useCallback(() => {
    setError("");
  }, []);

  const stopStream = useCallback(() => {
    if (!streamRef.current) {
      return;
    }

    streamRef.current.getTracks().forEach((track) => {
      track.stop();
    });

    streamRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    if (!client || !roomId) {
      setError("Matrix client или roomId отсутствует");
      return;
    }

    if (typeof MediaRecorder === "undefined") {
      setError("Браузер не поддерживает MediaRecorder");
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Браузер не поддерживает доступ к микрофону");
      return;
    }

    try {
      setError("");

      const stream =
        await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

      streamRef.current = stream;

      const supportedMimeType =
        getSupportedAudioMimeType();

      console.log(
        "[VOICE] supported MIME:",
        supportedMimeType || "browser default"
      );

      const recorder = supportedMimeType
        ? new MediaRecorder(stream, {
            mimeType: supportedMimeType,
          })
        : new MediaRecorder(stream);

      recorderRef.current = recorder;
      chunksRef.current = [];
      startedAtRef.current = Date.now();

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);

          console.log(
            "[VOICE] chunk:",
            event.data.size,
            event.data.type
          );
        }
      };

      recorder.onerror = (event) => {
        console.error(
          "[VOICE] MediaRecorder error:",
          event
        );

        setError("Ошибка MediaRecorder");
      };

      recorder.onstop = async () => {
        try {
          const chunks = chunksRef.current;

          const actualMimeType =
            recorder.mimeType ||
            supportedMimeType ||
            "audio/webm";

          const duration =
            startedAtRef.current
              ? Date.now() - startedAtRef.current
              : 0;

          console.log("[VOICE] stopped:", {
            chunks: chunks.length,
            mimeType: actualMimeType,
            duration,
          });

          stopStream();

          recorderRef.current = null;
          startedAtRef.current = null;

          setRecording(false);

          if (!chunks.length) {
            throw new Error("MediaRecorder не создал аудио");
          }

          const blob = new Blob(chunks, {
            type: actualMimeType,
          });

          console.log("[VOICE] blob:", {
            size: blob.size,
            type: blob.type,
          });

          if (!blob.size) {
            throw new Error("Получился пустой audio Blob");
          }

          const extension =
            getAudioExtension(actualMimeType);

          const fileName =
            `voice-${Date.now()}.${extension}`;

          const file = new File(
            [blob],
            fileName,
            {
              type: actualMimeType,
            }
          );

          console.log("[VOICE] file:", {
            name: file.name,
            type: file.type,
            size: file.size,
          });

          setUploading(true);
          setError("");

          // 1. СНАЧАЛА загружаем аудио в Matrix media repository
          console.log("[VOICE] Uploading...");

          const upload =
            await client.uploadContent(file, {
              type: actualMimeType,
              name: file.name,
            });

          console.log("[VOICE] upload response:", upload);

          const mxcUrl = upload?.content_uri;

          if (!mxcUrl) {
            throw new Error(
              "Matrix upload не вернул content_uri"
            );
          }

          console.log(
            "[VOICE] uploaded MXC:",
            mxcUrl
          );

          // 2. ПОТОМ отправляем m.audio
          console.log(
            "[VOICE] Sending m.audio..."
          );

          await client.sendMessage(roomId, {
            msgtype: "m.audio",
            body: file.name,
            url: mxcUrl,

            info: {
              mimetype: actualMimeType,
              size: file.size,
              duration,
            },
          });

          console.log(
            "[VOICE] m.audio sent successfully"
          );
        } catch (err) {
          console.error(
            "[VOICE] Audio send error:",
            err
          );

          setError(
            err?.message ||
              "Не удалось отправить аудио"
          );
        } finally {
          setUploading(false);
        }
      };

      recorder.start();

      setRecording(true);

      console.log(
        "[VOICE] Recording started"
      );
    } catch (err) {
      console.error(
        "[VOICE] Start recording error:",
        err
      );

      stopStream();

      recorderRef.current = null;
      setRecording(false);

      setError(
        err?.message ||
          "Не удалось начать запись"
      );
    }
  }, [
    client,
    roomId,
    stopStream,
  ]);

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;

    if (
      !recorder ||
      recorder.state === "inactive"
    ) {
      return;
    }

    console.log(
      "[VOICE] Stopping recorder..."
    );

    recorder.stop();
  }, []);

  useEffect(() => {
    return () => {
      const recorder = recorderRef.current;

      if (
        recorder &&
        recorder.state !== "inactive"
      ) {
        recorder.stop();
      }

      stopStream();
    };
  }, [stopStream]);

  return {
    recording,
    uploading,
    error,
    startRecording,
    stopRecording,
    clearError,
  };
}